
import { User } from '@features/settings/types/settings.types';
import api from '@shared/services/api';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { Post } from '../types/post.types';

interface CacheItem<T> {
  data: T;
  timestamp: number;
}

interface CommunityStore {
  // State
  randomUsers: User[];
  trendingPosts: Post[];
  loading: boolean;
  error: string | null;
  lastRandomUsersFetch: number;
  lastTrendingPostsFetch: number;

  // Actions
  getRandomUsers: (limit?: number, forceFetch?: boolean) => Promise<void>;
  getTrendingPosts: (limit?: number, forceFetch?: boolean) => Promise<void>;
  reset: () => void;
}

export const useCommunityStore = create<CommunityStore>()(
  devtools(
    (set) => ({
      // Initial state
      randomUsers: [],
      trendingPosts: [],
      loading: false,
      error: null,
      lastRandomUsersFetch: 0,
      lastTrendingPostsFetch: 0,

      // Actions
      getRandomUsers: async (limit = 4, forceFetch = false) => {
        const now = Date.now();
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

        try {
          const state = useCommunityStore.getState();
          const cacheAge = now - state.lastRandomUsersFetch;

          // Return cached data if it's fresh and not forcing fetch
          if (!forceFetch && cacheAge < CACHE_DURATION && state.randomUsers.length > 0) {
            return;
          }

          set({ loading: true, error: null });
          const response = await api.get<{ users: User[] }>(`/users/random?limit=${limit}`);
          set({
            randomUsers: response.data.users,
            lastRandomUsersFetch: now,
            loading: false
          });
        } catch (error) {
          set({ error: 'Error fetching random users', loading: false });
        }
      },

      getTrendingPosts: async (limit = 5, forceFetch = false) => {
        const now = Date.now();
        const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

        try {
          const state = useCommunityStore.getState();
          const cacheAge = now - state.lastTrendingPostsFetch;

          // Return cached data if it's fresh and not forcing fetch
          if (!forceFetch && cacheAge < CACHE_DURATION && state.trendingPosts.length > 0) {
            return;
          }

          set({ loading: true, error: null });
          const response = await api.get<{ posts: Post[] }>(`/posts/trending?limit=${limit}`);
          set({
            trendingPosts: response.data.posts,
            lastTrendingPostsFetch: now,
            loading: false
          });
        } catch (error) {
          set({ error: 'Error fetching trending posts', loading: false });
        }
      },

      reset: () => {
        set({
          randomUsers: [],
          trendingPosts: [],
          loading: false,
          error: null,
          lastRandomUsersFetch: 0,
          lastTrendingPostsFetch: 0
        });
        set({
          randomUsers: [],
          trendingPosts: [],
          loading: false,
          error: null
        });
      }
    }),
    { name: 'community-store' }
  )
);
